import React, { useState, useEffect } from "react";
import { useI18n } from "../context/I18nContext.jsx";
import { useAdminAuth } from "../context/AdminAuthContext.jsx";

const s = {
  wrap: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", padding: 20 },
  card: { background: "#fff", borderRadius: 16, padding: "40px 36px", width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
  logo: { textAlign: "center", marginBottom: 28 },
  title: { fontSize: 28, fontWeight: 700, color: "#1a1a2e", letterSpacing: -0.5 },
  sub: { fontSize: 14, color: "#6b7280", marginTop: 4 },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  label: { fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 4, display: "block" },
  input: { width: "100%", padding: "12px 14px", border: "1.5px solid #d1d5db", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  btn: { width: "100%", padding: "14px", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, color: "#fff", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", cursor: "pointer", marginTop: 4, fontFamily: "inherit" },
  err: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, padding: "10px 14px", color: "#991b1b", fontSize: 14, textAlign: "center" },
  hint: { fontSize: 12, color: "#6b7280", marginTop: 4 },
};

export default function LoginPage() {
  const { t } = useI18n();
  const { login } = useAdminAuth();
  const [needsSetup, setNeedsSetup] = useState(null); // null = loading, true = setup, false = login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch("/api/admin/setup-status")
      .then((r) => r.json())
      .then((d) => setNeedsSetup(d.needsSetup === true))
      .catch(() => setNeedsSetup(false));
  }, []);

  const handleSetup = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) return setError(t("login.fieldsRequired"));
    setBusy(true);
    try {
      const res = await fetch("/api/admin/setup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, name: name.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Aanmaken mislukt");
      // Setup succeeded — store token and reload as logged-in admin
      localStorage.setItem("zyncfit_admin_token", data.token);
      window.location.replace("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) return setError(t("login.fieldsRequired"));
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Loading state
  if (needsSetup === null) {
    return (
      <div style={s.wrap}>
        <div style={s.card}>
          <div style={s.logo}><div style={s.title}>ZyncFit</div></div>
          <div style={{ textAlign: "center", padding: 20, color: "#6b7280" }}>{t("common.loading")}</div>
        </div>
      </div>
    );
  }

  // Setup mode — no account exists yet
  if (needsSetup) {
    return (
      <div style={s.wrap}>
        <div style={s.card}>
          <div style={s.logo}>
            <div style={s.title}>ZyncFit</div>
            <div style={s.sub}>{t("login.setupTitle")}</div>
          </div>
          <form style={s.form} onSubmit={handleSetup}>
            {error && <div style={s.err}>{error}</div>}
            <div>
              <label style={s.label}>{t("common.name")}</label>
              <input style={s.input} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("login.namePlaceholder")} autoComplete="name" />
            </div>
            <div>
              <label style={s.label}>{t("common.email")}</label>
              <input style={s.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("login.emailPlaceholder")} autoComplete="email" autoFocus required />
            </div>
            <div>
              <label style={s.label}>{t("common.password")}</label>
              <input style={s.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("login.setupPasswordPlaceholder")} autoComplete="new-password" required />
              <div style={s.hint}>{t("login.passwordRequirements")}</div>
            </div>
            <button type="submit" style={{ ...s.btn, opacity: busy ? 0.7 : 1 }} disabled={busy}>
              {busy ? t("common.loading") : t("login.createAccount")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Login mode — account exists
  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.title}>ZyncFit</div>
          <div style={s.sub}>{t("login.subtitle")}</div>
        </div>
        <form style={s.form} onSubmit={handleLogin}>
          {error && <div style={s.err}>{error}</div>}
          <div>
            <label style={s.label}>{t("common.email")}</label>
            <input style={s.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("login.emailPlaceholder")} autoComplete="email" autoFocus />
          </div>
          <div>
            <label style={s.label}>{t("common.password")}</label>
            <input style={s.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("login.passwordPlaceholder")} autoComplete="current-password" />
          </div>
          <button type="submit" style={{ ...s.btn, opacity: busy ? 0.7 : 1 }} disabled={busy}>
            {busy ? t("login.loggingIn") : t("login.loginButton")}
          </button>
        </form>
      </div>
    </div>
  );
}
